import type { Editor } from '@tiptap/react'
import { findIndex } from 'rambda'
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

import { splitString } from '../constants'
import { FunctionObject, MethodList } from '../types'
import { interceptFormulaReverseText, interceptFormulaText, replaceText } from '../utils'
// import { methodList } from '../../constant'
import { FormulaDescribe } from './FormulaDescribe'
import * as SC from './styles'

interface ListItem {
    id?: string
    name: string
    icon?: string
    extra?: string
}

export enum FormulaMode {
    all = 0b0111,
    field = 0b0001,
    function = 0b0010,
    none = 0b0000
}

interface FormulaDefinitionsProps {
    editor: Editor | null
    mode: FormulaMode
    methodList: MethodList[]
    functionList: MethodList[]
    activeItem: FunctionObject | null
    onHoverFunction: (data: FunctionObject, isScroll?: boolean) => void
}

export const FormulaDefinitions = forwardRef<HTMLDivElement, FormulaDefinitionsProps>(
    ({ activeItem, editor, mode, methodList, functionList, onHoverFunction }, ref) => {
        const [mouseState, setMouseState] = useState(true)
        const list = useMemo(() => {
            return functionList.reduce<FunctionObject[]>((prev, cur) => {
                return [...prev, ...cur.list]
            }, [])
        }, [functionList])

        const functions = useMemo(() => {
            return methodList.reduce<Record<string, FunctionObject>>((prev, item) => {
                item.list.forEach(item => {
                    prev[item.name.toLocaleLowerCase()] = item
                })
                return prev
            }, {})
        }, [methodList])

        // const fieldList = useMemo(() => fields.filter(item => item.id !== fieldId), [fieldId, fields])

        const activateMouse = useCallback(() => {
            setMouseState(true)
        }, [])

        useEffect(() => {
            document.addEventListener('mousemove', activateMouse)
            return () => {
                document.removeEventListener('mousemove', activateMouse)
            }
        }, [activateMouse])

        const handleActive = useCallback(
            (active: FunctionObject, activeIndex: number) => {
                setMouseState(false)
                const len = list.length
                if (len === 0) {
                    return
                }

                const index = findIndex(item => item.id === active.id, list)
                // If current active isn't in list (can happen when list changes), fall back to first item.
                // const cur = index >= 0 ? index : 0
                const next = (index + activeIndex + len) % len
                onHoverFunction(list[next])
            },
            [list, onHoverFunction]
        )

        useHotkeys(
            'up, down, tab',
            (event, handler) => {
                switch (handler.key) {
                    case 'up': {
                        if (!activeItem) {
                            return
                        }
                        handleActive(activeItem, -1)
                        break
                    }
                    case 'down': {
                        if (!activeItem) {
                            return
                        }
                        handleActive(activeItem, 1)
                        // scrollActiveItem(activeItem.id)
                        break
                    }
                    case 'tab': {
                        event.preventDefault()
                        // event.defaultPrevented()
                        if (!activeItem) {
                            return false
                        }
                        handleClickAdapter(activeItem)
                        return false
                    }
                    default: {
                        break
                    }
                }
            },
            { enableOnTags: ['INPUT'], enableOnContentEditable: true },
            [activeItem, handleActive]
        )

        const getInstance = useCallback((nextText: string) => {
            // Only the token immediately after the caret can be an insertion point.
            // `) > ToNumber(Slice(` must not jump forward to the later parenthesis.
            for (const i of nextText) {
                if (splitString.has(i) || i === ')' || i === '>' || i === '<' || /\s/.test(i)) {
                    return -1
                }
            }
            const nextIndex = nextText.indexOf('(')
            if (nextText.startsWith('.')) {
                return 0
            }
            if (nextIndex >= 0) {
                return nextIndex + 1
            }
            return -1
        }, [])

        // const handleClickData = useCallback(
        //     (item: ListItem, type: 'field' | 'dataSource', dataType = 'dataSource') => {
        //         if (!editor) {
        //             return
        //         }
        //         // insertContentAt
        //         editor
        //             .chain()
        //             .focus()
        //             .insertContent([
        //                 {
        //                     type,
        //                     attrs: {
        //                         id: item.id,
        //                         icon: item.icon,
        //                         name: item.name,
        //                         extra: item.extra,
        //                         dataType
        //                     }
        //                 }
        //             ])
        //             .run()
        //     },
        //     [editor]
        // )

        const handleClickAdapter = useCallback(
            (item: FunctionObject) => {
                if (!editor) {
                    return
                }
                const {
                    view: {
                        state: { selection }
                    }
                } = editor
                const { $anchor, anchor } = selection
                const { nodeBefore, nodeAfter } = $anchor
                let insertText = item.fn
                let prevText = ''

                if (nodeBefore && nodeBefore.isText && nodeBefore.text) {
                    const text = replaceText(nodeBefore?.text)
                    const nodeBeforeText = [...text].reverse().join('')
                    prevText = interceptFormulaReverseText(nodeBeforeText, new Set([...splitString, '(', '.', ')']))
                }
                // if (range.length > 0) {
                //     const startIndex = anchor - (range[0] || 0)
                //     const stopIndex = anchor + (range[1] || 0)
                //     editor
                //         .chain()
                //         .focus()
                //         .deleteRange({ from: startIndex || 0, to: stopIndex })
                //         .insertContentAt(startIndex, insertText)
                //         .focus(startIndex + insertText.length - 1)
                //         .run()
                //     return
                // }

                if (nodeAfter && nodeAfter.isText && nodeAfter.text) {
                    const nodeAfterText = nodeAfter.text
                    const nextText = interceptFormulaText(nodeAfterText, new Set([...splitString, '(', '.', ')'])).toLocaleLowerCase()
                    if (functions?.[nextText]) {
                        const startIndex = anchor - prevText.length
                        const stopIndex = anchor
                        editor
                            .chain()
                            .focus()
                            .deleteRange({ from: startIndex, to: stopIndex })
                            .insertContent(insertText)
                            .focus(startIndex + insertText.length - 1)
                            .run()
                        return
                    }

                    // A typed prefix such as "Mon" is completed at the caret.
                    // Scanning ahead would insert Month() into a later call.
                    if (!prevText) {
                        const distance = getInstance(nodeAfterText)
                        if (distance >= 0) {
                            editor
                                .chain()
                                .focus()
                                .insertContentAt(anchor + distance, insertText)
                                .focus(anchor + distance + insertText.length - 1)
                                .run()
                            return
                        }
                    }
                }
                // // 添加node  到 括号中
                // 光标前是node 则添加 .方法()
                if (nodeBefore && nodeBefore.isText) {
                    const startIndex = anchor - prevText.length
                    const stopIndex = anchor
                    const deleteIndex = stopIndex - startIndex
                    editor
                        .chain()
                        .focus()
                        .deleteRange({ from: startIndex, to: stopIndex })
                        .insertContent(insertText)
                        .focus(anchor + insertText.length - 1 - deleteIndex)
                        .run()

                    return
                }
                if (nodeBefore && !nodeBefore.isText) {
                    insertText = `.${insertText}`
                }
                editor
                    .chain()
                    .focus()
                    .insertContent(insertText)
                    .focus(anchor + insertText.length - 1)
                    .run()
            },
            [editor, functions, getInstance]
        )

        return (
            <SC.FormulaDefinitions tabIndex={-1}>
                {mode === FormulaMode.none ? (
                    <SC.FormulaEmpty>
                        <SC.FormulaEmptyText>试试输入「.」，即可获得公式提示</SC.FormulaEmptyText>
                    </SC.FormulaEmpty>
                ) : (
                    <>
                        <SC.FormulaAdapters ref={ref}>
                            {functionList.map(item => (
                                <SC.AdaptersList key={item.name}>
                                    <SC.AdaptersHeader top={8}>{item.name}</SC.AdaptersHeader>
                                    {item.list.map(v => (
                                        <SC.AdaptersLi
                                            id={v.name}
                                            key={v.name}
                                            data-adapter={v.id}
                                            onMouseOver={() => mouseState && onHoverFunction(v, false)}
                                            active={activeItem?.id === v.id}
                                            onClick={() => handleClickAdapter(v)}
                                        >
                                            <SC.AdaptersContent>
                                                <SC.AdaptersIcon fill="var(--color-blue-500)" type="PropertyFormula" />
                                                <SC.AdaptersName>{v.name}</SC.AdaptersName>
                                            </SC.AdaptersContent>
                                            <SC.AdaptersType>
                                                <SC.AdaptersTagTab active={activeItem?.id === v.id}>Tab</SC.AdaptersTagTab>
                                            </SC.AdaptersType>
                                        </SC.AdaptersLi>
                                    ))}
                                </SC.AdaptersList>
                            ))}
                        </SC.FormulaAdapters>
                        <FormulaDescribe data={activeItem} />
                    </>
                )}
            </SC.FormulaDefinitions>
        )
    }
)
